import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  executeReceivingOperation,
  extractReceivingApiError,
  getReceivingContext,
} from "../../services/supplyChainReceivingApi";

import { useAdminScope } from "../../context/AdminScopeContext";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";

import { AdminLoadingState } from "../../pages/admin/ui_engine";
import { AdminEmptyState } from "../../pages/admin/ui_engine";
import { AdminErrorState } from "../../pages/admin/ui_engine";



const RECEIVING_OPERATIONS = Object.freeze([
  {
    value: "receive",
    label: "Recevoir",
  },
  {
    value: "partial_receive",
    label: "Réception partielle",
  },
  {
    value: "reject",
    label: "Refuser",
  },
  {
    value: "damage",
    label: "Signaler endommagé",
  },
  {
    value: "return_supplier",
    label: "Retour fournisseur",
  },
]);


function getScopeValue(scope, ...keys) {
  for (const key of keys) {
    if (scope?.[key]) {
      return scope[key];
    }
  }

  return "";
}


function formatQuantity(value) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return "0.000";
  }

  return String(value);
}


export default function SupplierReceivingAdminPanel({
  purchaseOrderReference: initialPurchaseOrderReference = "",
}) {
  const scope = useAdminScope();
  const panelActions = usePanelActions();

  const companyUuid = getScopeValue(
    scope,
    "companyUuid",
    "selectedCompanyUuid",
  );

  const activityUuid = getScopeValue(
    scope,
    "activityUuid",
    "selectedActivityUuid",
  );

  const [
    purchaseOrderReference,
    setPurchaseOrderReference,
  ] = useState(initialPurchaseOrderReference);

  const [
    loadedReference,
    setLoadedReference,
  ] = useState("");

  const [context, setContext] = useState(null);
  const [selectedLineReference, setSelectedLineReference] =
    useState("");
  const [operationKey, setOperationKey] =
    useState("receive");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [lastExecution, setLastExecution] =
    useState(null);
  const [status, setStatus] = useState("initial");
  const [errorMessage, setErrorMessage] =
    useState("");
  const [successMessage, setSuccessMessage] =
    useState("");
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const requestControllerRef = useRef(null);

  const lines = useMemo(
    () => (
      Array.isArray(context?.lines)
        ? context.lines
        : []
    ),
    [context],
  );

  const selectedLine = useMemo(
    () => (
      lines.find(
        (line) => (
          line.line_reference === selectedLineReference
        ),
      ) ?? null
    ),
    [
      lines,
      selectedLineReference,
    ],
  );

  const resetOperation = useCallback(() => {
    setSelectedLineReference("");
    setOperationKey("receive");
    setQuantity("");
    setReason("");
    setLastExecution(null);
    setSuccessMessage("");
  }, []);

  const loadContext = useCallback(
    async (reference = purchaseOrderReference) => {
      const normalizedReference =
        typeof reference === "string"
          ? reference.trim()
          : "";

      if (!normalizedReference) {
        setContext(null);
        setLoadedReference("");
        setStatus("initial");
        setErrorMessage("");
        return;
      }

      if (!companyUuid || !activityUuid) {
        setContext(null);
        setStatus("error");
        setErrorMessage(
          "Le scope société et activité est requis.",
        );
        return;
      }

      requestControllerRef.current?.abort();

      const controller = new AbortController();
      requestControllerRef.current = controller;

      setStatus("loading");
      setErrorMessage("");
      setSuccessMessage("");

      try {
        const nextContext = await getReceivingContext({
          purchaseOrderReference: normalizedReference,
          companyUuid,
          activityUuid,
          signal: controller.signal,
        });

        setContext(nextContext);
        setLoadedReference(normalizedReference);
        setStatus(
          Array.isArray(nextContext?.lines)
          && nextContext.lines.length > 0
            ? "ready"
            : "empty",
        );

        setSelectedLineReference((current) => {
          if (
            nextContext?.lines?.some(
              (line) => line.line_reference === current,
            )
          ) {
            return current;
          }

          return (
            nextContext?.lines?.[0]?.line_reference
            ?? ""
          );
        });
      } catch (error) {
        if (error?.name === "CanceledError") {
          return;
        }

        if (error?.name === "AbortError") {
          return;
        }

        setContext(null);
        setStatus("error");
        setErrorMessage(
          extractReceivingApiError(
            error,
            "Impossible de charger la réception.",
          ),
        );
      }
    },
    [
      activityUuid,
      companyUuid,
      purchaseOrderReference,
    ],
  );

  const handleLoad = useCallback(
    async (event) => {
      event.preventDefault();
      resetOperation();
      await loadContext();
    },
    [
      loadContext,
      resetOperation,
    ],
  );

  const handleSubmitOperation = useCallback(
    async (event) => {
      event.preventDefault();

      if (isSubmitting) {
        return;
      }

      if (!loadedReference) {
        setErrorMessage(
          "Chargez d'abord une commande fournisseur.",
        );
        return;
      }

      if (!selectedLineReference) {
        setErrorMessage(
          "Sélectionnez une ligne de commande.",
        );
        return;
      }

      if (!quantity.trim()) {
        setErrorMessage(
          "La quantité est obligatoire.",
        );
        return;
      }

      setIsSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      try {
        const execution =
          await executeReceivingOperation({
            purchaseOrderReference: loadedReference,
            operationKey,
            lineReference: selectedLineReference,
            quantity,
            reason,
            metadata: {
              source: "supplier_receiving_admin_panel",
            },
            companyUuid,
            activityUuid,
          });

        setLastExecution(execution);

        await loadContext(loadedReference);

        setSuccessMessage(
          "Opération de réception enregistrée.",
        );
      } catch (error) {
        setErrorMessage(
          extractReceivingApiError(
            error,
            "Impossible d'enregistrer la réception.",
          ),
        );

        await loadContext(loadedReference);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      activityUuid,
      companyUuid,
      isSubmitting,
      loadContext,
      loadedReference,
      operationKey,
      quantity,
      reason,
      selectedLineReference,
    ],
  );

  useEffect(() => {
    if (
      initialPurchaseOrderReference
      && companyUuid
      && activityUuid
    ) {
      loadContext(initialPurchaseOrderReference);
    }

    return () => {
      requestControllerRef.current?.abort();
    };
  }, [
    activityUuid,
    companyUuid,
    initialPurchaseOrderReference,
    loadContext,
  ]);

  useEffect(() => {
    if (!panelActions?.setActions) {
      return undefined;
    }

    panelActions.setActions([
      {
        key: "refresh",
        label: "Actualiser",
        onClick: () => loadContext(loadedReference),
        disabled: !loadedReference || status === "loading",
      },
      {
        key: "reset",
        label: "Réinitialiser",
        onClick: () => {
          setPurchaseOrderReference("");
          setLoadedReference("");
          setContext(null);
          setStatus("initial");
          setErrorMessage("");
          resetOperation();
        },
      },
    ]);

    return () => {
      panelActions.setActions([]);
    };
  }, [
    loadContext,
    loadedReference,
    panelActions,
    resetOperation,
    status,
  ]);

  return (
    <section
      aria-labelledby="supplier-receiving-title"
      data-testid="supplier-receiving-admin-panel"
    >
      <header>
        <h2 id="supplier-receiving-title">
          Réception fournisseur
        </h2>

        <p>
          Contrôlez les quantités reçues à partir
          d'une commande fournisseur.
        </p>
      </header>

      <form onSubmit={handleLoad}>
        <label htmlFor="receiving-purchase-order-reference">
          Référence de commande
        </label>

        <input
          id="receiving-purchase-order-reference"
          name="purchaseOrderReference"
          value={purchaseOrderReference}
          onChange={(event) => {
            setPurchaseOrderReference(
              event.target.value,
            );
          }}
          disabled={status === "loading"}
          autoComplete="off"
        />

        <button
          type="submit"
          disabled={
            status === "loading"
            || !purchaseOrderReference.trim()
          }
        >
          Charger
        </button>
      </form>

      {status === "loading" && (
        <AdminLoadingState
          message="Chargement de la commande fournisseur…"
        />
      )}

      {status === "error" && (
        <AdminErrorState
          message={errorMessage}
          onRetry={() => loadContext()}
        />
      )}

      {status === "empty" && (
        <AdminEmptyState
          title="Aucune ligne à réceptionner"
          description={
            "La commande ne contient aucune ligne exploitable."
          }
        />
      )}

      {status === "ready" && (
        <>
          <div>
            <strong>
              Commande :
            </strong>{" "}
            {context?.receipt_reference
              ?? loadedReference}
          </div>

          <div role="region" aria-label="Lignes de réception">
            <table>
              <thead>
                <tr>
                  <th scope="col">Sélection</th>
                  <th scope="col">Ligne</th>
                  <th scope="col">Commandé</th>
                  <th scope="col">Reçu</th>
                  <th scope="col">Restant</th>
                  <th scope="col">Accepté</th>
                  <th scope="col">Refusé</th>
                  <th scope="col">Endommagé</th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line) => (
                  <tr key={line.line_reference}>
                    <td>
                      <input
                        type="radio"
                        name="receiving-line"
                        value={line.line_reference}
                        checked={
                          selectedLineReference
                          === line.line_reference
                        }
                        onChange={() => {
                          setSelectedLineReference(
                            line.line_reference,
                          );
                          setQuantity(
                            formatQuantity(
                              line.remaining_quantity,
                            ),
                          );
                        }}
                        aria-label={
                          `Sélectionner ${line.line_reference}`
                        }
                      />
                    </td>

                    <td>{line.line_reference}</td>
                    <td>
                      {formatQuantity(
                        line.expected_quantity,
                      )}
                    </td>
                    <td>
                      {formatQuantity(
                        line.received_quantity,
                      )}
                    </td>
                    <td>
                      {formatQuantity(
                        line.remaining_quantity,
                      )}
                    </td>
                    <td>
                      {formatQuantity(
                        line.accepted_quantity,
                      )}
                    </td>
                    <td>
                      {formatQuantity(
                        line.rejected_quantity,
                      )}
                    </td>
                    <td>
                      {formatQuantity(
                        line.damaged_quantity,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form onSubmit={handleSubmitOperation}>
            <fieldset disabled={isSubmitting}>
              <legend>
                Opération de réception
              </legend>

              <label htmlFor="receiving-operation">
                Opération
              </label>

              <select
                id="receiving-operation"
                value={operationKey}
                onChange={(event) => {
                  setOperationKey(event.target.value);
                }}
              >
                {RECEIVING_OPERATIONS.map(
                  (operation) => (
                    <option
                      key={operation.value}
                      value={operation.value}
                    >
                      {operation.label}
                    </option>
                  ),
                )}
              </select>

              <label htmlFor="receiving-quantity">
                Quantité
              </label>

              <input
                id="receiving-quantity"
                type="number"
                min="0.001"
                step="0.001"
                inputMode="decimal"
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.target.value);
                }}
                max={
                  selectedLine?.remaining_quantity
                    ?? undefined
                }
              />

              <label htmlFor="receiving-reason">
                Motif
              </label>

              <textarea
                id="receiving-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />

              <button
                type="submit"
                disabled={
                  isSubmitting
                  || !selectedLineReference
                  || !quantity.trim()
                }
              >
                {isSubmitting
                  ? "Enregistrement…"
                  : "Valider l'opération"}
              </button>
            </fieldset>
          </form>

          {successMessage && (
            <p role="status">
              {successMessage}
            </p>
          )}

          {errorMessage && status === "ready" && (
            <div role="alert">
              {errorMessage}
            </div>
          )}

          {lastExecution?.operation && (
            <section aria-label="Dernière opération">
              <h3>Dernière opération</h3>

              <dl>
                <dt>Type</dt>
                <dd>
                  {lastExecution.operation.operation_key}
                </dd>

                <dt>Quantité</dt>
                <dd>
                  {formatQuantity(
                    lastExecution.operation.quantity,
                  )}
                </dd>

                <dt>Impact stock</dt>
                <dd>
                  {formatQuantity(
                    lastExecution.operation
                      .inventory_quantity_delta,
                  )}
                </dd>
              </dl>
            </section>
          )}
        </>
      )}
    </section>
  );
}
